import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    TextField,
} from '@material-ui/core';
import PaneHeader from '../../../PaneHeader';
import Translator from '../../../../helpers/TranslationHelper';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import TimeFormatter from '../../../../helpers/TimeFormatter';
import React, { ChangeEvent, FocusEvent, useEffect, useState } from 'react';
import useToggle from '../../../../hooks/useToggle';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import {
    IRecalculateData,
    recalcuateData,
} from '../../../../state/app/monitoredObjects';
import { hideGlobalDialog } from '../../../../state/ui/dialog';
import { useAppDispatch } from '../../../../hooks';
import { IReportEntity } from '../../../../state/types';
import { useSnackbar } from 'notistack';

interface IOwnProps {
    data: IReportEntity;
    monitoredId: string;
    dateFormat: string;
}

interface IState {
    showDialog: boolean;
    recalculated?: boolean;
    form: IRecalculateData;
}

export const ObjectRecalculationDialog = ({
    data,
    monitoredId,
    dateFormat,
}: IOwnProps) => {
    const currentDate = new Date(new Date().setHours(0, 0, 0, 0));
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const { isOpen, handleToggle } = useToggle(true);

    useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            recalculated: false,
        }));
    }, [data.actions?.onClick?.disabled]);

    const [state, setState] = useState<IState>({
        showDialog: false,
        recalculated: data.actions?.onClick?.disabled,
        form: {
            start: currentDate,
            reason: '',
        },
    });

    const handleDateChange = (date: MaterialUiPickersDate | null) => {
        if (date) {
            date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            setState({
                ...state,
                form: {
                    ...state.form,
                    start: date.toDate(),
                },
            });
        }
    };

    const handleReasonChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.slice(0, 100);
        setState({
            ...state,
            form: {
                ...state.form,
                reason: value,
            },
        });
    };

    const handleReasonBlur = (event: FocusEvent<HTMLInputElement>) => {
        const value = event.target.value.trim();
        setState({
            ...state,
            form: {
                ...state.form,
                reason: value,
            },
        });
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setState({
            ...state,
            recalculated: true,
        });
        dispatch(
            recalcuateData(
                state.form,
                monitoredId,
                () => {
                    enqueueSnackbar(
                        Translator.translate('Recalculating state started'),
                        {
                            variant: 'success',
                        }
                    );
                    handleClose();
                },
                () => {
                    enqueueSnackbar(Translator.translate('Unknown error'), {
                        variant: 'error',
                    });
                    handleClose();
                }
            )
        );
    };

    const handleClose = (reason?: string) => {
        if (reason === 'backdropClick') {
            return;
        }
        dispatch(hideGlobalDialog());
        handleToggle();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={(event?: object, reason?: string) => {
                handleClose(reason);
            }}
        >
            <form onSubmit={handleSubmit}>
                <PaneHeader
                    title={Translator.translate(data.label)}
                    onCloseClick={() => handleClose()}
                />
                <DialogContent>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                        <DatePicker
                            format={dateFormat}
                            value={state.form.start}
                            onChange={handleDateChange}
                            label={Translator.translate('Date')}
                            fullWidth={true}
                            required={true}
                            disableFuture={true}
                            minDate={TimeFormatter.subtractedDate(31)}
                        />
                    </MuiPickersUtilsProvider>

                    <TextField
                        fullWidth={true}
                        multiline={true}
                        rows={4}
                        label={Translator.translate('Reason')}
                        margin="normal"
                        required={true}
                        value={state.form.reason}
                        onChange={handleReasonChange}
                        onBlur={handleReasonBlur}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleClose()} variant="contained">
                        {Translator.translate('Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={state.recalculated}
                    >
                        {Translator.translate('Recalculate')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
export default ObjectRecalculationDialog;
